import { useEffect, useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import {
  UserPlus,
  Users,
  Zap,
  Eye,
  TrendingUp,
  Globe,
  Trophy,
  CheckCircle,
  CheckSquare,
  Rocket,
  Award,
  Flame,
} from 'lucide-react';
import type { UserBadge, BadgeTier } from '../../types/gamification';
import { TIER_LABELS, TIER_COLORS } from '../../types/gamification';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  UserPlus,
  Users,
  Magnet: Zap,
  Zap,
  Eye,
  TrendingUp,
  Globe,
  Trophy,
  CheckCircle,
  CheckSquare,
  Rocket,
  Award,
  Flame,
};

interface BadgeUnlockedToastProps {
  badge: UserBadge;
  onClose: () => void;
  autoHide?: number;
}

export function BadgeUnlockedToast({
  badge,
  onClose,
  autoHide = 5000,
}: BadgeUnlockedToastProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation
    const showTimer = setTimeout(() => setIsVisible(true), 50);

    // Auto-hide
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, autoHide);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [autoHide, onClose]);

  if (!badge.badge) return null;

  const IconComponent = ICONS[badge.badge.icon] || Trophy;
  const tierColors = TIER_COLORS[badge.badge.tier as BadgeTier];

  return (
    <div
      className={`
        fixed bottom-4 right-4 z-50
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
      `}
    >
      <div
        className={`
          relative flex items-center gap-4 px-4 py-3 rounded-xl shadow-lg border
          bg-white dark:bg-slate-800 ${tierColors.border}
          min-w-[280px] max-w-sm
        `}
      >
        {/* Sparkle decoration */}
        <div className="absolute -top-2 -left-2">
          <Sparkles className="w-5 h-5 text-yellow-500 animate-pulse" />
        </div>

        {/* Badge Icon */}
        <div className={`flex-shrink-0 ${tierColors.bg} rounded-lg p-2`}>
          <IconComponent className={`w-8 h-8 ${tierColors.text}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-primary mb-0.5">
            Neues Badge freigeschaltet!
          </p>
          <h4 className="font-semibold text-gray-900 dark:text-white truncate">
            {badge.badge.name}
          </h4>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            {TIER_LABELS[badge.badge.tier as BadgeTier]}
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>

        {/* Animated border glow */}
        <div
          className={`
            absolute inset-0 rounded-xl opacity-50 pointer-events-none
            animate-pulse ${tierColors.border} border-2
          `}
        />
      </div>
    </div>
  );
}
